import {
  Alert,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  Button,
  ExternalLinkIcon,
  Link,
  TabItemProps,
  useToast,
} from '@razorpay/blade/components';
import api from 'api';
import { routePaths } from 'components/Routes/data';
import { GenericDeclaration } from 'components/ui/DeclarationWindow/types';
import Errors from 'components/ui/Form/Errors';
import TabLayout from 'components/ui/Layout/TabLayout';
import { links } from 'constants/flexibenefits';
import React, { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import { AppError } from 'utils/AppError';
import { showToastViaEvent } from 'utils/ToastEvents';
import DeclarationSettings from './DeclarationSettings/DeclarationSettings';
import GeneralSettings from './GeneralSettings';
import {
  GET_FLEXIBLE_BENEFITS_SETTINGS,
  UPDATE_FLEXIBLE_BENEFITS_SETTINGS,
  UPDATE_FLEXIBLE_BENEFITS_SETTINGS_V2,
} from './queries';
import { type FlexiBenefitsSettings } from './types';
import useFBPValidations from './useFBPValidations';
import {
  createPayload,
  parseSettings,
  parseListEffectiveSettings,
  transformToBulkUpdateFormat,
} from './utils';
import AdvancedWindowConfiguration from './AdvancedWindowConfiguration/AdvancedWindowConfiguration';
import useIsFeatureDisabledForRearch from 'access/useIsFeatureDisabledForRearch';
import useFeature from 'hooks/useFeature';
import { FBPSettingsDescriptions } from './constants';

const FlexibleBenefitsSettings = () => {
  const location = useLocation();
  const org = useSelector(loggedInUserSelectors.currentOrganization);
  const pathname = location.pathname;
  const containerRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  const [settings, setSettings] = useState<FlexiBenefitsSettings>({
    shouldHoldFlexiAmount: false,
    employeeCanDeclareFlexiBenefits: false,
    employeeCanUpdateAmountInDeclaredFlexiBenefits: false,
    employeeDeclarationWindow: { type: 'always' },
    employeeProofUploadWindow: { type: 'always' },
    FbpDefaultTaxable: false,
    isAutoReleaseEnabledForUnclaimedBenefits: false,
    isTaxableAfterMonthEnabled: false,
    isAutoOpenWindowForNewEmployeesEnabled: false,
    isAutoOpenWindowForSalaryRevisedEmployeesEnabled: false,
    allowProofsUploadTillDismissal: false,
  });

  const { isFbpExcludeFromSaAdjustmentEnabled } = useFeature('fbpExcludeFromSaAdjustment');

  const getTabValue = () => {
    switch (pathname) {
      case routePaths.flexibleBenefits.settings:
      case routePaths.flexibleBenefits.settingsGeneral:
        return isFbpExcludeFromSaAdjustmentEnabled ? 'declaration_window' : 'general';
      case routePaths.flexibleBenefits.settingsDeclaration:
        return 'declaration_window';
      case routePaths.flexibleBenefits.settingsProofUpload:
        return 'proof_upload_window';
      case routePaths.flexibleBenefits.settingsWindowAdavanced:
        return 'advanced_window_config';
    }
  };
  const [currentTab, setCurrentTab] = useState(getTabValue());

  const history = useHistory();
  const { isM3S2Enabled } = useIsFeatureDisabledForRearch();

  const { isLoading, isRefetching, refetch, error } = useQuery({
    queryKey: GET_FLEXIBLE_BENEFITS_SETTINGS,
    queryFn: () =>
      org?.id ? api.flexibleBenefits.getSettings(org.id) : Promise.reject('User id is required'),
    onSuccess: (data) => {
      setSettings(parseSettings(data));
    },
    onError: (e: typeof AppError) => {},
    enabled: !isM3S2Enabled,
  });

  const {
    isLoading: isLoadingEffectiveSettings,
    isRefetching: isRefetchingEffectiveSettings,
    error: errorEffectiveSettings,
    refetch: refetchEffectiveSettings,
  } = useQuery({
    queryKey: ['LIST_EFFECTIVE_SETTINGS_V2'],
    queryFn: () =>
      api.flexibleBenefitComponents.queries.listEffectiveSettings({
        page: 0,
        page_size: 100,
      }),
    onSuccess: (response) => {
      if (response?.payrollListEffectiveSettings?.settings) {
        const parsedSettings = parseListEffectiveSettings(
          response.payrollListEffectiveSettings.settings,
        );
        setSettings((prev) => ({ ...prev, ...parsedSettings }));
      }
    },
    enabled: isM3S2Enabled,
    staleTime: 0,
    cacheTime: 0,
    refetchOnMount: 'always',
  });

  const updateSettings = useMutation({
    mutationKey: UPDATE_FLEXIBLE_BENEFITS_SETTINGS,
    mutationFn: api.flexibleBenefits.updateSettings,
    onSuccess: (data) => {
      setSettings(parseSettings(data));
      showToastViaEvent({
        text: 'Configuration saved successfully!',
        timeout: 3000,
        type: 'success',
        icon: 'success',
      });
    },
    onError: (e: typeof AppError) => {
      window.requestAnimationFrame(() => {
        containerRef.current?.scrollIntoView();
      });
    },
  });

  const updateSettingsV2 = useMutation({
    mutationKey: UPDATE_FLEXIBLE_BENEFITS_SETTINGS_V2,
    mutationFn: api.flexibleBenefitComponents.mutations.bulkUpdateSettings,
    onSuccess: async (data) => {
      const responseData = data?.payrollBulkUpdateSettings?.data;

      if (responseData?.success) {
        showToastViaEvent({
          text: responseData.message || 'Configuration saved successfully!',
          timeout: 3000,
          type: 'success',
          icon: 'success',
        });

        await queryClient.invalidateQueries(['LIST_EFFECTIVE_SETTINGS_V2'], {
          refetchActive: true,
          refetchInactive: false,
        });
      } else {
        showToastViaEvent({
          text: responseData?.error_message || 'Failed to save configuration!',
          timeout: 3000,
          type: 'danger',
          icon: 'info',
        });
      }
    },
    onError: (e: typeof AppError) => {
      window.requestAnimationFrame(() => {
        containerRef.current?.scrollIntoView();
      });
    },
  });

  const resetUpdate = updateSettings.reset;

  const handleGeneralSettingsChange = (
    key: keyof FlexiBenefitsSettings,
    value: string | boolean,
  ) => {
    setSettings((p) => ({ ...p, [key]: value }));
  };

  const { errors, validate, resetValidations } = useFBPValidations({ settings });

  const handleSave = () => {
    const { isValid } = validate();
    if (!isValid || !org?.id) return;

    if (isM3S2Enabled) {
      const bulkUpdates = transformToBulkUpdateFormat(settings);
      updateSettingsV2.mutate({ updates: bulkUpdates });
    } else {
      updateSettings.mutate({
        org: org?.id,
        settings: createPayload(settings),
      });
    }
  };

  const tabItems: TabItemProps[] = [
    ...(isFbpExcludeFromSaAdjustmentEnabled
      ? []
      : [
          {
            children: 'General',
            value: 'general',
            onClick: () => history.push(routePaths.flexibleBenefits.settingsGeneral),
          },
        ]),
    {
      children: 'Declaration Window',
      value: 'declaration_window',
      onClick: () => history.push(routePaths.flexibleBenefits.settingsDeclaration),
    },
    {
      children: 'Proof Upload Window',
      value: 'proof_upload_window',
      onClick: () => history.push(routePaths.flexibleBenefits.settingsProofUpload),
    },
    {
      children: 'Advance Window Configuration',
      value: 'advanced_window_config',
      onClick: () => history.push(routePaths.flexibleBenefits.settingsWindowAdavanced),
    },
  ];

  const handleDeclarationSettingsChange = (
    value: GenericDeclaration,
    isProofUploadWindowSettings: boolean,
  ) => {
    setSettings((p) => ({
      ...p,
      [isProofUploadWindowSettings ? 'employeeProofUploadWindow' : 'employeeDeclarationWindow']:
        value,
    }));
  };

  const handleWindowPeriodChange = () => resetValidations();

  const isUpdating = updateSettings.isLoading || updateSettingsV2.isLoading;

  const isLoadingData = isM3S2Enabled ? isLoadingEffectiveSettings : isLoading;
  const isRefetchingData = isM3S2Enabled ? isRefetchingEffectiveSettings : isRefetching;
  const errorDescription = isM3S2Enabled
    ? updateSettingsV2.error || errorEffectiveSettings
    : updateSettings.error || error;

  const isDeclarationSettingsView = pathname === routePaths.flexibleBenefits.settingsDeclaration;

  const isDeclrataionDisabled = isM3S2Enabled
    ? false
    : settings.employeeCanDeclareFlexiBenefits === false;

  const isSaveHidden = isDeclarationSettingsView && isDeclrataionDisabled;

  const handleClick = () =>
    window.open(links.learnMoreAboutFbp[isM3S2Enabled ? 'M3S2' : 'DEFAULT'], '_blank');

  const handleTabChange = (value: string) => {
    resetUpdate();
    setCurrentTab(value);
    if (isM3S2Enabled) {
      refetchEffectiveSettings();
    } else {
      refetch();
    }
  };

  const handleAdvancedSettingsChange = (
    key: keyof FlexiBenefitsSettings,
    value: string | boolean,
  ) => {
    setSettings((p) => ({ ...p, [key]: value }));
  };

  return (
    <TabLayout
      tabs={tabItems}
      isLoading={isLoadingData || isRefetchingData}
      navbarProps={{
        tabsProps: {
          onChange: handleTabChange,
          value: currentTab,
          variant: 'borderless',
        },
        navContainerProps: {
          borderTopWidth: 'thin',
          borderTopColor: 'surface.border.gray.normal',
          paddingTop: 'spacing.3',
        },
      }}
      TabRightSideLayout={
        <Link variant="button" icon={ExternalLinkIcon} iconPosition="right" onClick={handleClick}>
          How to setup FBP
        </Link>
      }
      BreadcrumbComponent={
        <Box marginY={'spacing.5'}>
          <Breadcrumb>
            <BreadcrumbItem href="/settings" accessibilityLabel="Settings">
              Settings
            </BreadcrumbItem>
            <BreadcrumbItem
              isCurrentPage
              href="/settings/flexible-benefits"
              accessibilityLabel="Settings">
              Flexible Benefit Plan Setup
            </BreadcrumbItem>
          </Breadcrumb>
        </Box>
      }>
      <Box width="100%" height="100%">
        {errorDescription ? (
          <Box
            ref={containerRef}
            marginX={{ base: 'spacing.8', l: 'auto' }}
            width="500px"
            paddingTop="spacing.8"
            backgroundColor="surface.background.gray.moderate">
            <Alert
              description={<Errors errorResponse={errorDescription as typeof AppError} />}
              color="negative"
            />
          </Box>
        ) : null}
        <Switch>
          <Route path={routePaths.flexibleBenefits.settingsWindowAdavanced}>
            <AdvancedWindowConfiguration
              orgId={org?.id}
              isLoading={updateSettings.isLoading}
              settings={settings}
              onChange={handleAdvancedSettingsChange}
            />
          </Route>
          <Route path={routePaths.flexibleBenefits.settingsDeclaration}>
            <DeclarationSettings
              isLoading={updateSettings.isLoading}
              settings={settings}
              errors={errors.employeeDeclarationWindow || {}}
              title="🪟 Declaration window"
              onChange={(val) => handleDeclarationSettingsChange(val, false)}
              description="Define the period during which employees can make changes to their flexible benefit selections."
              onWindowPeriodChange={handleWindowPeriodChange}
            />
          </Route>
          <Route path={routePaths.flexibleBenefits.settingsProofUpload}>
            <DeclarationSettings
              isLoading={isUpdating}
              settings={settings}
              errors={errors.employeeProofUploadWindow || {}}
              title="📄 Proof Upload Window"
              onChange={(val) => handleDeclarationSettingsChange(val, true)}
              description={
                FBPSettingsDescriptions['proof-upload-window-config'][
                  isM3S2Enabled ? 'M3S2' : 'DEFAULT'
                ]
              }
              isProofUploadWindowSettings
              onWindowPeriodChange={handleWindowPeriodChange}
            />
          </Route>
          <Route
            path={[
              routePaths.flexibleBenefits.settings,
              routePaths.flexibleBenefits.settingsGeneral,
            ]}>
            {isFbpExcludeFromSaAdjustmentEnabled ? (
              <Redirect to={routePaths.flexibleBenefits.settingsDeclaration} />
            ) : (
              <GeneralSettings
                isLoading={isUpdating}
                settings={settings}
                onChange={handleGeneralSettingsChange}
              />
            )}
          </Route>
          <Route>
            <Redirect to={routePaths.unknown} />
          </Route>
        </Switch>

        {isSaveHidden ? null : (
          <Box paddingBottom="spacing.11" marginX={{ base: 'spacing.8', l: 'auto' }} width="500px">
            <Button
              isDisabled={isLoadingData || isRefetchingData || !errors.isValid}
              isLoading={isUpdating}
              onClick={handleSave}>
              Save & Confirm
            </Button>
          </Box>
        )}
      </Box>
    </TabLayout>
  );
};

export default FlexibleBenefitsSettings;
